import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthProvider";

export function useCurrentSubscription() {
  const { user } = useAuth();
  const [plan, setPlan] = useState<
    "PLAN GRATIS" | "PLAN IMPULSO" | "PLAN VISION"
  >("PLAN GRATIS");
  const [status, setStatus] = useState<"active" | "expired" | "none">("none");
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [accountCount, setAccountCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [loading, setLoading] = useState(true);

  const limits = {
    "PLAN GRATIS": 3,
    "PLAN IMPULSO": 10,
    "PLAN VISION": Infinity,
  };

  useEffect(() => {
    if (!user?.id) return;

    const fetchAll = async () => {
      setLoading(true);

      // Paso 1: Obtener la suscripción (si existe)
      const { data: subscription } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "paid")
        .order("next_payment_date", { ascending: false })
        .limit(1)
        .single();

      let userPlan: "PLAN GRATIS" | "PLAN IMPULSO" | "PLAN VISION" =
        "PLAN GRATIS";

      if (subscription) {
        userPlan =
          (subscription?.plan_name?.toUpperCase() as typeof userPlan) ||
          "PLAN GRATIS";

        const next = new Date(subscription.next_payment_date);
        const now = new Date();
        const diff = Math.ceil(
          (next.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
        );
        setDaysLeft(diff);
      } else {
        setDaysLeft(null);
      }

      setPlan(userPlan);

      // Paso 2: Obtener número de cuentas del usuario
      const { data: business } = await supabase
        .from("businesses")
        .select("id")
        .eq("user_id", user.id)
        .single();

      let count = 0;
      if (business) {
        const { data: accounts } = await supabase
          .from("accounts")
          .select("id")
          .eq("business_id", business.id);
        count = accounts?.length || 0;
      }
      setAccountCount(count);

      // Paso 3: Calcular límites
      const limit = limits[userPlan];
      const hasReachedLimit = count >= limit;
      setLimitReached(hasReachedLimit);

      // Paso 4: Establecer status según lógica de plan
      if (userPlan === "PLAN GRATIS") {
        setStatus(hasReachedLimit ? "expired" : "active");
      } else if (subscription) {
        const now = new Date();
        const next = new Date(subscription.next_payment_date);
        setStatus(next > now ? "active" : "expired");
      } else {
        setStatus("none");
      }

      setLoading(false);
    };

    fetchAll();
  }, [user?.id]);

  return {
    plan,
    status,
    daysLeft,
    accountCount,
    limitReached,
    canCreateAccount: !limitReached && status === "active",
    loading,
  };
}
